import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  CertificateRulesState,
  ExpressionNode,
  GetCertificateFunctionMappingsResponse,
} from '../../pages/settings/tabs/certificateRules/types';
import {
  getCertificateFunctionMappings,
  upsertCertificateFunctionMapping,
  deleteCertificateFunctionMapping,
} from '../../pages/settings/tabs/certificateRules/api';
import { RootState } from '../types';

// ─── Initial State ──────────────────────────────────────────────────────────────

export const initialState: CertificateRulesState = {
  mappings: [],
  availableFunctions: [],
  rulesEnabled: false,
  status: 'idle',
  saving: false,
  error: undefined,
};

const messageOf = (error: unknown, fallback: string): string =>
  error instanceof Error ? error.message : fallback;

// ─── Async Thunks ───────────────────────────────────────────────────────────────

export const fetchCertificateRules = createAsyncThunk<
  GetCertificateFunctionMappingsResponse,
  string,
  { rejectValue: string }
>(
  'certificateRules/fetchCertificateRules',
  async (warehouseId: string, { rejectWithValue }) => {
    try {
      return await getCertificateFunctionMappings(warehouseId);
    } catch (error: unknown) {
      return rejectWithValue(messageOf(error, 'Failed to fetch certificate rules'));
    }
  }
);

export interface SaveCertificateRuleArgs {
  warehouseId: string;
  requestedBy: string;
  processId: string;
  functionId: string;
  expression: ExpressionNode;
  version?: number;
}

export const saveCertificateRule = createAsyncThunk<void, SaveCertificateRuleArgs, { rejectValue: string }>(
  'certificateRules/saveCertificateRule',
  async (args, { dispatch, rejectWithValue }) => {
    try {
      await upsertCertificateFunctionMapping(args);
      dispatch(fetchCertificateRules(args.warehouseId));
    } catch (error: unknown) {
      return rejectWithValue(messageOf(error, 'Failed to save certificate rule'));
    }
  }
);

export interface DeleteCertificateRuleArgs {
  warehouseId: string;
  requestedBy: string;
  processId: string;
  functionId: string;
  version: number;
}

export const deleteCertificateRule = createAsyncThunk<void, DeleteCertificateRuleArgs, { rejectValue: string }>(
  'certificateRules/deleteCertificateRule',
  async (args, { dispatch, rejectWithValue }) => {
    try {
      await deleteCertificateFunctionMapping(args);
      dispatch(fetchCertificateRules(args.warehouseId));
    } catch (error: unknown) {
      return rejectWithValue(messageOf(error, 'Failed to delete certificate rule'));
    }
  }
);

// ─── Slice ──────────────────────────────────────────────────────────────────────

const certificateRulesSlice = createSlice({
  name: 'certificateRules',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchCertificateRules.pending, (state) => {
        state.status = 'loading';
        state.error = undefined;
      })
      .addCase(fetchCertificateRules.fulfilled, (state, action) => {
        state.status = 'success';
        state.mappings = action.payload.mappings || [];
        state.availableFunctions = action.payload.availableFunctions || [];
        state.rulesEnabled = !!action.payload.rulesEnabled;
      })
      .addCase(fetchCertificateRules.rejected, (state, action) => {
        state.status = 'error';
        state.error = action.payload || 'Failed to fetch certificate rules';
      })
      .addCase(saveCertificateRule.pending, (state) => {
        state.saving = true;
      })
      .addCase(saveCertificateRule.fulfilled, (state) => {
        state.saving = false;
      })
      .addCase(saveCertificateRule.rejected, (state) => {
        state.saving = false;
      })
      .addCase(deleteCertificateRule.pending, (state) => {
        state.saving = true;
      })
      .addCase(deleteCertificateRule.fulfilled, (state) => {
        state.saving = false;
      })
      .addCase(deleteCertificateRule.rejected, (state) => {
        state.saving = false;
      });
  },
});

// ─── Selectors ──────────────────────────────────────────────────────────────────

export const selectCertificateRules = (state: RootState) => state.certificateRules.mappings;
export const selectAvailableFunctions = (state: RootState) => state.certificateRules.availableFunctions;
export const selectCertificateRulesStatus = (state: RootState) => state.certificateRules.status;
export const selectCertificateRulesState = (state: RootState) => state.certificateRules;

export default certificateRulesSlice.reducer;
